import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  NJBadge,
  NJButton,
  NJCard,
  NJCardBody,
  NJModal,
  NJModalContent,
  NJHeading,
  NJFormItem,
  NJIconButton,
  NJMenu,
  NJText,
  NJTooltip,
} from '@engie-group/fluid-design-system-react';
import type { DiscoveredPrototype } from '../shared/discoverPrototypes';
import { EDITABLE_STATUSES, isEditableStatus } from '../shared/prototypeStatus';
import type { PrototypeStatus } from '../shared/prototypeStatus';
import { comparePrototypesByUpdatedAt, formatUpdatedAt } from '../shared/prototypeDates';
import { useStudioState } from './home/useStudioState';
import { fetchCommentStats, getPrototypeCommentStats, type CommentStats } from '../shared/bffComments';

const PREVIEW_MODULES = import.meta.glob<{ default: string }>(
  '../prototypes/plan2cost/*/preview.png',
  { eager: true },
);

const STATUS_LABELS: Record<Exclude<PrototypeStatus, 'archived'>, string> = {
  reference: 'Référence',
  delivered: 'Livré',
  ready: 'Prêt',
  review: 'En revue',
  draft: 'Brouillon',
};

const STATUS_VARIANTS = {
  reference: 'success',
  delivered: 'information',
  ready: 'success',
  review: 'warning',
  draft: 'neutral',
} as const;

interface Plan2CostHomeProps {
  prototypes: DiscoveredPrototype[];
}

function PrototypeCard({
  proto,
  title,
  description,
  status,
  onChangeStatus,
  onSaveDetails,
  isArchived,
  isDeleted,
  onArchive,
  onRestoreArchive,
  onDelete,
  onRestoreDelete,
  commentStats,
  commentsError,
}: {
  proto: DiscoveredPrototype;
  title: string;
  description: string;
  status: Exclude<PrototypeStatus, 'archived'>;
  onChangeStatus: (slug: string, status: Exclude<PrototypeStatus, 'archived'>) => void;
  onSaveDetails: (slug: string, title: string, description: string) => Promise<void>;
  isArchived: boolean;
  isDeleted: boolean;
  onArchive: (slug: string) => void;
  onRestoreArchive: (slug: string) => void;
  onDelete: (slug: string) => void;
  onRestoreDelete: (slug: string) => void;
  commentStats?: CommentStats;
  commentsError: boolean;
}) {
  const creator = `Créé par : ${proto.meta.author || 'Non renseigné'}`;
  const lastModified = `Dernière modification : ${formatUpdatedAt(proto.meta.updatedAt)}`;
  const previewSrc = PREVIEW_MODULES[`../prototypes/plan2cost/${proto.slug}/preview.png`]?.default;
  const [menuOpened, setMenuOpened] = useState(false);
  const [editing, setEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  const [descriptionDraft, setDescriptionDraft] = useState(description);

  const startEditing = () => {
    setTitleDraft(title);
    setDescriptionDraft(description);
    setMenuOpened(false);
    setEditing(true);
  };

  const cancelEditing = () => {
    setTitleDraft(title);
    setDescriptionDraft(description);
    setEditing(false);
  };

  const saveDetails = async () => {
    await onSaveDetails(proto.slug, titleDraft, descriptionDraft);
    setEditing(false);
  };

  return (
    <>
    <NJCard hasBottomBorder style={{ minHeight: 320 }}>
      <div style={{ height: 148, position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        <Link
          to={`/plan2cost/prototype/${proto.slug}`}
          aria-label={`Prévisualiser ${proto.meta.title}`}
          style={{ display: 'block', height: '100%' }}
        >
          {previewSrc ? (
            <img
              src={previewSrc}
              alt={`Aperçu — ${proto.meta.title}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }}
            />
          ) : (
            <div
              aria-hidden="true"
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--nj-semantic-color-icon-neutral-secondary-default)',
                background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 40 }}>image</span>
            </div>
          )}
        </Link>
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'inline-flex' }}>
          {isArchived || isDeleted ? (
            <NJBadge variant="neutral" emphasis="subtle">
              {isDeleted ? 'Corbeille' : 'Archivé'}
            </NJBadge>
          ) : (
            <label style={{ position: 'relative', display: 'inline-flex' }}>
              <NJBadge variant={STATUS_VARIANTS[status]} emphasis="subtle">
                {STATUS_LABELS[status]}
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14, marginLeft: 4 }}>
                  expand_more
                </span>
              </NJBadge>
              <select
                aria-label={`Statut de ${proto.meta.title}`}
                value={status}
                onChange={(event) => {
                  if (isEditableStatus(event.target.value)) {
                    onChangeStatus(proto.slug, event.target.value);
                  }
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                }}
              >
                {EDITABLE_STATUSES.map((value) => (
                  <option key={value} value={value}>{STATUS_LABELS[value]}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>
      <NJCardBody style={{ display: 'flex', flex: 1, minHeight: 0, padding: 'var(--nj-semantic-size-spacing-16)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          <Link
            to={`/plan2cost/prototype/${proto.slug}`}
            style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
          >
            <NJHeading as="h3" scale="sm" style={{ margin: '0 0 var(--nj-semantic-size-spacing-8)' }}>
              <NJText as="span" scale="sm" style={clampedTextStyle(2)}>
                {title}
              </NJText>
            </NJHeading>
            <NJTooltip text={description} placement="top" hasArrow>
              <NJText as="span" scale="sm" style={clampedTextStyle(2)} tabIndex={0}>
                {description}
              </NJText>
            </NJTooltip>
          </Link>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 'var(--nj-semantic-size-spacing-8)',
            margin: 'var(--nj-semantic-size-spacing-12) 0',
          }}>
            {commentsError ? (
              <NJText scale="sm" variant="secondary">Statistiques indisponibles</NJText>
            ) : !commentStats ? (
              <NJText scale="sm" variant="secondary">Chargement des discussions…</NJText>
            ) : (
              <>
                <NJBadge variant={commentStats.open ? 'warning' : 'neutral'} emphasis="subtle">
                  {commentStats.open} discussion{commentStats.open !== 1 ? 's' : ''} ouverte{commentStats.open !== 1 ? 's' : ''}
                </NJBadge>
                <NJText as="span" scale="xs" variant="secondary">
                  {commentStats.resolved} résolue{commentStats.resolved !== 1 ? 's' : ''}
                </NJText>
              </>
            )}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 'var(--nj-semantic-size-spacing-8)',
            marginTop: 'auto',
            minWidth: 0,
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              gap: 'var(--nj-semantic-size-spacing-4)',
              minWidth: 0,
            }}>
              <NJText as="span" scale="xs" variant="secondary" style={clampedTextStyle(1)}>
                {creator}
              </NJText>
              <NJText as="span" scale="xs" variant="secondary" style={clampedTextStyle(1)}>
                {lastModified}
              </NJText>
            </div>
            <NJMenu.Root opened={menuOpened} onOpen={setMenuOpened}>
              <NJMenu.Anchor>
                <NJIconButton
                  label={`Actions de ${proto.meta.title}`}
                  title="Actions"
                  icon="more_vert"
                  variant="tertiary"
                  scale="sm"
                />
              </NJMenu.Anchor>
              <NJMenu.Dropdown aria-label={`Actions de ${proto.meta.title}`} style={{ minWidth: 200 }}>
                {isDeleted ? (
                  <NJMenu.Item
                    label={`Rétablir ${proto.meta.title}`}
                    onClick={() => { setMenuOpened(false); onRestoreDelete(proto.slug); }}
                  >
                    Rétablir le prototype
                  </NJMenu.Item>
                ) : (
                  <>
                    <NJMenu.Item label={`Modifier ${title}`} onClick={startEditing}>
                      Modifier
                    </NJMenu.Item>
                    <NJMenu.Item
                      label={`${isArchived ? 'Rétablir' : 'Archiver'} ${proto.meta.title}`}
                      onClick={() => {
                        setMenuOpened(false);
                        isArchived ? onRestoreArchive(proto.slug) : onArchive(proto.slug);
                      }}
                    >
                      {isArchived ? 'Rétablir des archives' : 'Archiver'}
                    </NJMenu.Item>
                    <NJMenu.Item
                      label={`Mettre ${proto.meta.title} à la corbeille`}
                      onClick={() => { setMenuOpened(false); onDelete(proto.slug); }}
                    >
                      Mettre à la corbeille
                    </NJMenu.Item>
                  </>
                )}
              </NJMenu.Dropdown>
            </NJMenu.Root>
          </div>
        </div>
      </NJCardBody>
    </NJCard>
    <NJModal
      isOpen={editing}
      setIsOpen={(open) => { if (!open) cancelEditing(); }}
      closeOnEscape
      closeOnBackdropClick
    >
      <NJModalContent
        id={`edit-plan-prototype-${proto.slug}`}
        title="Modifier le prototype"
        shouldFitViewport
        onClose={cancelEditing}
        onBackdropClick={cancelEditing}
        onEscapeKey={cancelEditing}
        appendTo="body"
        footer={
          <>
            <NJButton
              label="Annuler"
              variant="secondary"
              onClick={cancelEditing}
            />
            <NJButton
              label="Enregistrer"
              onClick={() => { void saveDetails(); }}
              disabled={!titleDraft.trim()}
            />
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--nj-semantic-size-spacing-16)' }}>
          <NJFormItem
            id={`plan-title-${proto.slug}`}
            label="Titre"
            value={titleDraft}
            maxLength={120}
            size="sm"
            onChange={(event) => setTitleDraft((event.target as HTMLInputElement).value)}
          />
          <NJFormItem
            id={`plan-description-${proto.slug}`}
            label="Description"
            value={descriptionDraft}
            isMultiline
            rows={4}
            maxLength={2000}
            size="sm"
            onChange={(event) => setDescriptionDraft((event.target as HTMLTextAreaElement).value)}
          />
        </div>
      </NJModalContent>
    </NJModal>
    </>
  );
}

function clampedTextStyle(lines: number): CSSProperties {
  return {
    display: '-webkit-box',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
  };
}

export default function Plan2CostHome({ prototypes }: Plan2CostHomeProps) {
  const studio = useStudioState();
  const [openOnly, setOpenOnly] = useState(false);
  const comments = useQuery({
    queryKey: ['comment-stats'],
    queryFn: fetchCommentStats,
    enabled: studio.ready && prototypes.length > 0,
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const sortedPrototypes = [...prototypes].sort(comparePrototypesByUpdatedAt);
  const changeStatus = (slug: string, status: Exclude<PrototypeStatus, 'archived'>) => {
    const original = prototypes.find((proto) => proto.slug === slug)?.meta.status ?? 'draft';
    return studio.setStatus(slug, status === original ? null : status);
  };
  const activePrototypes = sortedPrototypes.filter((proto) =>
    !studio.isDeleted(proto.slug) && !studio.isArchived(proto.slug) &&
    !(proto.meta.status === 'archived' && !studio.isUnarchived(proto.slug))
  );
  const archivedPrototypes = sortedPrototypes.filter((proto) =>
    !studio.isDeleted(proto.slug) && (
      studio.isArchived(proto.slug) ||
      (proto.meta.status === 'archived' && !studio.isUnarchived(proto.slug))
    )
  );
  const deletedPrototypes = sortedPrototypes.filter((proto) => studio.isDeleted(proto.slug));
  const statsFor = (slug: string) => comments.data && getPrototypeCommentStats(comments.data, slug);
  const prototypesWithOpenDiscussions = activePrototypes.filter((proto) => (statsFor(proto.slug)?.open ?? 0) > 0);
  const visiblePrototypes = openOnly ? prototypesWithOpenDiscussions : activePrototypes;
  const totals = activePrototypes.reduce((total, proto) => {
    const stats = statsFor(proto.slug);
    total.open += stats?.open ?? 0;
    total.resolved += stats?.resolved ?? 0;
    return total;
  }, { open: 0, resolved: 0 });
  const archive = (slug: string) => studio.setArchived(slug, true);
  const restoreArchive = (slug: string) => studio.setArchived(slug, false);
  const deletePrototype = (slug: string) => studio.setDeleted(slug, true);
  const restoreDeleted = (slug: string) => studio.setDeleted(slug, false);
  const saveDetails = async (slug: string, title: string, description: string) => {
    await studio.setTitle(slug, title);
    await studio.setDescription(slug, description);
  };

  const renderCards = (items: DiscoveredPrototype[]) => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: 'var(--nj-semantic-size-spacing-16)',
    }}>
      {items.map((proto) => {
        const title = studio.getTitle(proto.slug) ?? proto.meta.title;
        const description = studio.getDescription(proto.slug) ?? proto.meta.description;
        const override = studio.getStatus(proto.slug);
        const status = isEditableStatus(override)
          ? override
          : proto.meta.status === 'archived' ? 'draft' : proto.meta.status ?? 'draft';
        return (
          <PrototypeCard
            key={proto.slug}
            proto={proto}
            title={title}
            description={description}
            status={status}
            onChangeStatus={changeStatus}
            onSaveDetails={saveDetails}
            isArchived={studio.isArchived(proto.slug) || (proto.meta.status === 'archived' && !studio.isUnarchived(proto.slug))}
            isDeleted={studio.isDeleted(proto.slug)}
            onArchive={archive}
            onRestoreArchive={restoreArchive}
            onDelete={deletePrototype}
            onRestoreDelete={restoreDeleted}
            commentStats={statsFor(proto.slug)}
            commentsError={comments.isError}
          />
        );
      })}
    </div>
  );

  return (
    <div style={{
      padding: 'var(--nj-semantic-size-spacing-40)',
      maxWidth: 1000,
      margin: '0 auto',
    }}>
      <NJButton
        label="Accueil"
        href="/"
        icon="home"
        emphasis="subtle"
        scale="sm"
        aria-label="Retour à l'accueil Prototyper Studio"
      />
      <NJHeading as="h1" scale="lg" style={{ margin: 'var(--nj-semantic-size-spacing-16) 0 var(--nj-semantic-size-spacing-8)' }}>
        Plan — Studio
      </NJHeading>
      <NJText as="div" style={{ marginBottom: 'var(--nj-semantic-size-spacing-32)', maxWidth: 720 }}>
        Plan prolonge UrbanMap : un projet est une copie figée d'un périmètre UrbanMap (bâtiments, routes, réseaux),
        modifiable, partageable et duplicable, sur laquelle on pose des hypothèses et on lance des scénarios de plan de développement.
      </NJText>
      {studio.ready && prototypes.length > 0 && (
        <section aria-label="Suivi des discussions" style={{ marginBottom: 'var(--nj-semantic-size-spacing-24)' }}>
          <NJHeading as="h2" scale="sm">Discussions des prototypes actifs</NJHeading>
          <div aria-live="polite">
            {comments.isError ? (
              <NJText role="alert">Impossible de charger les statistiques des commentaires.</NJText>
            ) : comments.isPending ? (
              <NJText variant="secondary">Chargement des statistiques des commentaires…</NJText>
            ) : (
              <NJText>
                {totals.open} discussion{totals.open !== 1 ? 's' : ''} ouverte{totals.open !== 1 ? 's' : ''}
                {' · '}{prototypesWithOpenDiscussions.length} prototype{prototypesWithOpenDiscussions.length !== 1 ? 's' : ''} à traiter
                {' · '}{totals.resolved} discussion{totals.resolved !== 1 ? 's' : ''} résolue{totals.resolved !== 1 ? 's' : ''}
              </NJText>
            )}
          </div>
          <NJText scale="sm" variant="secondary">
            Sous-vues incluses ; les réponses ne sont pas comptées comme de nouvelles discussions.
          </NJText>
          <div role="group" aria-label="Filtrer par discussions" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--nj-semantic-size-spacing-8)',
            marginTop: 'var(--nj-semantic-size-spacing-12)',
          }}>
            <NJButton label="Tous les prototypes" scale="sm" variant={openOnly ? 'secondary' : 'primary'}
              aria-pressed={!openOnly} onClick={() => setOpenOnly(false)} />
            <NJButton label="Questions ouvertes" scale="sm" variant={openOnly ? 'primary' : 'secondary'}
              aria-pressed={openOnly} onClick={() => setOpenOnly(true)}
              disabled={comments.isPending || comments.isError} />
            <NJButton label={comments.isError ? 'Réessayer' : 'Actualiser les statistiques'} scale="sm" variant="secondary" emphasis="minimal"
              onClick={() => { void comments.refetch(); }} disabled={comments.isFetching} />
          </div>
        </section>
      )}
      <section>
        <NJHeading as="h2" scale="md" style={{ marginBottom: 'var(--nj-semantic-size-spacing-16)' }}>
          Prototypes ({visiblePrototypes.length})
        </NJHeading>
        {!studio.ready ? (
          <NJText variant="secondary">Chargement des prototypes…</NJText>
        ) : activePrototypes.length === 0 ? (
          <NJText variant="secondary">Aucun prototype pour le moment.</NJText>
        ) : openOnly && comments.isError ? (
          <NJText variant="secondary">Filtrage indisponible : réessayez le chargement ou affichez tous les prototypes.</NJText>
        ) : visiblePrototypes.length === 0 ? (
          <NJText variant="secondary">Aucun prototype actif avec une discussion ouverte.</NJText>
        ) : (
          renderCards(visiblePrototypes)
        )}
      </section>
      {studio.ready && (
        <>
          <section style={{ marginTop: 'var(--nj-semantic-size-spacing-40)' }}>
            <NJHeading as="h2" scale="md" style={{ marginBottom: 'var(--nj-semantic-size-spacing-16)' }}>
              Archives ({archivedPrototypes.length})
            </NJHeading>
            {archivedPrototypes.length ? renderCards(archivedPrototypes) : (
              <NJText variant="secondary">Aucun prototype archivé.</NJText>
            )}
          </section>
          <section style={{ marginTop: 'var(--nj-semantic-size-spacing-40)' }}>
            <NJHeading as="h2" scale="md" style={{ marginBottom: 'var(--nj-semantic-size-spacing-16)' }}>
              Corbeille ({deletedPrototypes.length})
            </NJHeading>
            {deletedPrototypes.length ? renderCards(deletedPrototypes) : (
              <NJText variant="secondary">Aucun prototype dans la corbeille.</NJText>
            )}
          </section>
        </>
      )}
    </div>
  );
}
